/**
 * Client for the iOS shell bridge (shared/bridge.ts). Every call is a no-op
 * (resolving to null) in a plain browser, so callers never need to branch.
 */
import type { BridgeEvent, BridgeInfo, BridgeRequest, HomeNative, LiveActivityKind, LiveActivityPrefs, LocationStatus, SensorId } from '@home/shared';

type HapticStyle = Extract<BridgeRequest, { cmd: 'haptic' }>['style'];
type DistributiveOmit<T, K extends keyof T> = T extends unknown ? Omit<T, K> : never;
type Req = DistributiveOmit<BridgeRequest, 'id'>;

declare global {
  interface Window {
    homeNative?: Partial<HomeNative>;
    webkit?: { messageHandlers?: { home?: { postMessage: (m: unknown) => void } } };
  }
}

let seq = 0;
const waiting = new Map<number, { resolve: (v: unknown) => void; reject: (e: Error) => void; timer: number }>();
const listeners = new Set<(e: BridgeEvent) => void>();
let installed = false;

export const isNative = (): boolean =>
  typeof window !== 'undefined' && (!!window.homeNative || !!window.webkit?.messageHandlers?.home);

function install(): void {
  if (installed || !isNative()) return;
  installed = true;
  const n = (window.homeNative ??= {});
  n._resolve = (id, result) => {
    const w = waiting.get(id);
    if (!w) return;
    waiting.delete(id);
    window.clearTimeout(w.timer);
    w.resolve(result);
  };
  n._reject = (id, error) => {
    const w = waiting.get(id);
    if (!w) return;
    waiting.delete(id);
    window.clearTimeout(w.timer);
    w.reject(new Error(error));
  };
  n._emit = (event) => listeners.forEach((fn) => fn(event));
}

function post(req: BridgeRequest): void {
  const n = window.homeNative;
  if (n && typeof n.postMessage === 'function') n.postMessage(req);
  else window.webkit?.messageHandlers?.home?.postMessage(req);
}

export function nativeCall<T>(req: Req, timeoutMs = 8000): Promise<T | null> {
  if (!isNative()) return Promise.resolve(null);
  install();
  const id = ++seq;
  return new Promise<T | null>((resolve, reject) => {
    const timer = window.setTimeout(() => {
      waiting.delete(id);
      resolve(null);
    }, timeoutMs);
    waiting.set(id, { resolve: resolve as (v: unknown) => void, reject, timer });
    try {
      post({ ...req, id } as BridgeRequest);
    } catch (e) {
      waiting.delete(id);
      window.clearTimeout(timer);
      reject(e instanceof Error ? e : new Error(String(e)));
    }
  });
}

export function onNativeEvent(fn: (e: BridgeEvent) => void): () => void {
  install();
  listeners.add(fn);
  return () => listeners.delete(fn);
}

/** Fire-and-forget haptic. Falls back to navigator.vibrate on Android browsers. */
export function haptic(style: HapticStyle = 'light'): void {
  if (isNative()) {
    void nativeCall({ cmd: 'haptic', style }, 1000).catch(() => undefined);
    return;
  }
  try {
    if ('vibrate' in navigator) navigator.vibrate(style === 'heavy' || style === 'warning' ? 20 : 8);
  } catch {
    /* ignore */
  }
}

export const native = {
  info: () => nativeCall<BridgeInfo>({ cmd: 'info' }),
  share: (text?: string, url?: string) =>
    nativeCall<void>({ cmd: 'share', ...(text ? { text } : {}), ...(url ? { url } : {}) }),
  sensors: () => nativeCall<{ available: SensorId[]; enabled: SensorId[]; location?: LocationStatus } | SensorId[]>({ cmd: 'sensors.get' }),
  /** iOS: fix location permission (prompts or Settings app); long timeout — the user answers a prompt. */
  locationFix: () => nativeCall<LocationStatus>({ cmd: 'location.fix' }, 120_000),
  setSensors: (enabled: SensorId[]) => nativeCall<void>({ cmd: 'sensors.set', enabled }),
  registerPush: () => nativeCall<void>({ cmd: 'push.register' }),
  nearestHome: () => nativeCall<string | { homeId: string | null } | null>({ cmd: 'location.nearestHome' }, 1500),
  openSettings: () => nativeCall<void>({ cmd: 'openSettings' }),
  liveActivities: () => nativeCall<LiveActivityPrefs>({ cmd: 'liveActivities.get' }),
  setLiveActivities: (kinds: Partial<Record<LiveActivityKind, boolean>>) =>
    nativeCall<LiveActivityPrefs>({ cmd: 'liveActivities.set', kinds }),
};

/** Tell the native shell which web build it loaded (HA `web_build` sensor). Fire-and-forget: a browser no-ops,
 *  and older apps reject the unknown command, which is swallowed. */
export function reportWebBuild(build: string = __BUILD_ID__): void {
  if (!isNative()) return;
  void nativeCall({ cmd: 'web.build', build }, 4000).catch(() => undefined);
}

/** After pairing: ask the iOS shell to turn native tablet mode on for `tablet` (keep-awake, idle return to /t/<id>).
 *  Never throws: a browser no-ops, and older apps (unknown command) / timeouts are swallowed. */
export async function enableNativeTablet(tablet: string, timeoutMs = 3000): Promise<void> {
  if (!isNative()) return;
  await nativeCall({ cmd: 'tablet.enable', tablet }, timeoutMs).catch(() => undefined);
}

/** Resolves the nearest home id from the native geofence, or null (browser / unknown). */
export async function nearestHome(): Promise<string | null> {
  try {
    const r = await native.nearestHome();
    if (typeof r === 'string') return r;
    if (r && typeof r === 'object' && 'homeId' in r) return r.homeId;
    return null;
  } catch {
    return null;
  }
}
